import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Divider,
  withStyles,
  createStyles,
  WithStyles,
  Theme,
  Checkbox,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import CheckIcon from '@material-ui/icons/Check';

import locales from '../../../locales';
import { TBookingFilters, TFilterListItem, TSort } from '../../../types/IBookingStore';
import { filterBookingListBy, sortBookingList } from '../../../actions/booking/BookingActions';
import classNames from "classnames";

const styles = (theme: Theme) => createStyles({
  dialog: {
    [theme.breakpoints.down(600)]: {
      margin: theme.spacing(1),
    },
  },
  dialogPaper: {
    [theme.breakpoints.down(600)]: {
      margin: theme.spacing(1),
      maxWidth: 'calc(100% - 16px)',
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  titleText: {
    fontSize: 18,
    fontWeight: 500,
  },
  dialogContent: {
    padding: theme.spacing(2),
    minHeight: 200,
  },
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#aaaaaa',
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
  },
  sortButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sortButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #E5E5E5',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  sortButtonActive: {
    backgroundColor: '#e3f2fd',
    borderColor: theme.palette.primary.main,
  },
  sortButtonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: '1px solid #E5E5E5',
  },
  applyButton: {
    minWidth: 120,
  },
});

interface IProps extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
  sort: TSort;
  filters: TBookingFilters;
  handleSortBookingList: typeof sortBookingList;
  handleFilterBy: typeof filterBookingListBy;
}

const FilterSortModal: React.FC<IProps> = ({
  classes,
  open,
  onClose,
  sort,
  filters,
  handleSortBookingList,
  handleFilterBy,
}) => {
  const [localFilters, setLocalFilters] = useState<Array<TFilterListItem>>(filters);
  const [localSort, setLocalSort] = useState<TSort>(sort);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    setLocalSort(sort);
  }, [sort]);

  /** @description Обработка изменения фильтров */
  const handleFilterChange = (updatedFilters: Array<TFilterListItem>) => {
    setLocalFilters(updatedFilters);
  };

  /** @description Обработка изменения сортировки */
  const handleSortChange = (orderBy: string, order: 'asc' | 'desc') => {
    setLocalSort({ orderBy, order });
  };

  /** @description Применение изменений */
  const handleApply = () => {
    // Применяем фильтры
    if (JSON.stringify(localFilters) !== JSON.stringify(filters)) {
      handleFilterBy(localFilters);
    }
    // Применяем сортировку
    if (localSort.orderBy !== sort.orderBy || localSort.order !== sort.order) {
      handleSortBookingList(localSort);
    }
    onClose();
  };

  /** @description Сброс к исходным значениям */
  const handleReset = () => {
    setLocalFilters(filters);
    setLocalSort(sort);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{
        root: classes.dialog,
        paper: classes.dialogPaper,
      }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.titleText}>
          {locales.t('booking.filters.title')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Сортировка по времени */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle} variant="overline">
            ВРЕМЯ
          </Typography>
          <div className={classes.sortButtons}>
            <div
              className={classNames(classes.sortButton, {
                [classes.sortButtonActive]: localSort.orderBy === 'date' && localSort.order === 'asc'
              })}
              onClick={() => handleSortChange('date', 'asc')}
            >
              <div className={classes.sortButtonContent}>
                <ArrowUpwardIcon fontSize="small" />
                <Typography>По возрастанию</Typography>
              </div>
              {localSort.orderBy === 'date' && localSort.order === 'asc' && (
                <CheckIcon color="primary" />
              )}
            </div>
            <div
              className={classNames(classes.sortButton, {
                [classes.sortButtonActive]: localSort.orderBy === 'date' && localSort.order === 'desc'
              })}
              onClick={() => handleSortChange('date', 'desc')}
            >
              <div className={classes.sortButtonContent}>
                <ArrowDownwardIcon fontSize="small" />
                <Typography>По убыванию</Typography>
              </div>
              {localSort.orderBy === 'date' && localSort.order === 'desc' && (
                <CheckIcon color="primary" />
              )}
            </div>
          </div>
        </div>

        <Divider />

        {/* Фильтры по статусу */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle} variant="overline">
            СТАТУС
          </Typography>
          <List>
            {localFilters.map((item) => (
              <ListItem
                key={item.value}
                button
                onClick={() => {
                  const updatedFilters = localFilters.map(
                    (i) => i.value === item.value
                      ? { value: item.value, active: !item.active }
                      : i,
                  );
                  handleFilterChange(updatedFilters);
                }}
              >
                <Checkbox
                  checked={item.active}
                  color="primary"
                />
                <ListItemText primary={locales.t(`booking.filters.${item.value}`)} />
              </ListItem>
            ))}
          </List>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleReset} color="default">
          Сбросить
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          className={classes.applyButton}
        >
          Применить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(FilterSortModal);
